import i18next from 'i18next';
import * as Sentry from "@sentry/react"
import { isBuildEnvironment, versionCompare } from './utils';

export const MINIMUM_WEBAPP_VERSION = '9.0'

/**
 * Retrieves the Telegram WebApp object from the global environment if available and meets the minimum version requirement.
 *
 * @param checkVersion - Whether to enforce the minimum WebApp version check (default: true).
 * @returns The Telegram WebApp object if present and valid; otherwise, returns null.
 *
 * @remark If the Telegram environment is unavailable, the WebApp is not initialized, or the version is insufficient (when {@link checkVersion} is true), the function logs the error to Sentry and returns null.
 */
function _getTelegramWebApp(checkVersion: boolean = true) {
    try {
        if (!window.Telegram) {
            throw new Error('App is not running in a Telegram environment')
        }

        if (!window.Telegram.WebApp) {
            throw new Error('Telegram WebApp is not initialized')
        }

        if (checkVersion && versionCompare(window.Telegram.WebApp.version, MINIMUM_WEBAPP_VERSION) < 0) {
            throw new Error('Telegram WebApp version is less than ' + MINIMUM_WEBAPP_VERSION)
        }

        return window.Telegram.WebApp
    } catch (error) {
        // Only report to Sentry if we're not in a build environment
        if (!isBuildEnvironment()) {
            const err = {
                error,
                version: window.Telegram?.WebApp?.version,
                platform: window.Telegram?.WebApp?.platform
            }
            Sentry.captureException(err)
        }
        return null
    }
}

export function disableSwipeClose() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.disableVerticalSwipes()
}

export function expandScreen() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.expand()
}

export function enableClosingConfirmation(enable: boolean = true) {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    if (enable) {
        webApp.enableClosingConfirmation()
    } else {
        webApp.disableClosingConfirmation()
    }
}

export function close() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.close()
}

export function openLink(link: string) {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.openLink(link)
}

export function hapticFeedback(impact?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.HapticFeedback.impactOccurred(impact || 'light')
}

export function setHeaderColor(color: string) {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.setHeaderColor(color)
}

export function APIVersion() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    return webApp.version
}

export function getPlatform() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    return webApp.platform
}

export function lockOrientation() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.lockOrientation()
}

export function onEvent(eventType: string, callback: () => void) {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.onEvent(eventType, callback)
}

export function offEvent(eventType: string, callback: () => void) {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }
    webApp.offEvent(eventType, callback)
}

export function getUserTelegramId() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }

    return webApp.initDataUnsafe?.user?.id
}

export function getStartParam() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return null
    }

    return webApp.initDataUnsafe?.start_param || null
}

export function getReferralCodeFromUrl() {
    // Check URL parameters for referral code
    const urlParams = new URLSearchParams(window.location.search)
    const refParam = urlParams.get('ref')

    // Also check Telegram's start_param as fallback
    if (!refParam) {
        const startParam = getStartParam()
        if (startParam && startParam.startsWith('ref_')) {
            return startParam.substring(4) // Remove 'ref_' prefix
        }
    }

    return refParam
}

export function generateReferralLink(referralCode: string) {
    const botLink = import.meta.env.VITE_TELEGRAM_BOT_LINK

    if (!botLink) {
        console.error('Bot link not configured')
        return null
    }

    // Generate Telegram bot link with start parameter
    return `${botLink}?startapp=ref_${encodeURIComponent(referralCode)}`
}

export function shareUrl(url: string, text?: string) {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return
    }

    webApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(url)}${text ? `&text=${encodeURIComponent(text)}` : ''}`)
}

export function safeAreaInset() {
    const webApp = _getTelegramWebApp(false)
    if (!webApp) {
        return {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        }
    }
    return webApp.safeAreaInset || {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
    }
}

export class Biometrics {
    /**
     * Gets the BiometricManager from Telegram WebApp if available
     * @returns The BiometricManager object or null if not available
     */
    private static getBiometricManager() {
        try {
            const webApp = _getTelegramWebApp()
            if (!webApp) {
                return null
            }

            if (!webApp.BiometricManager) {
                throw new Error('Biometrics manager is not initialized')
            }

            return webApp.BiometricManager
        } catch (error) {
            return null
        }
    }

    private static setBiometricsToken(onSuccess?: (token: string) => void, onError?: (error: any) => void) {
        try {
            const biometricManager = this.getBiometricManager()
            if (biometricManager) {
                const token = parseInt(Math.random().toString().substr(2)).toString(16);
                biometricManager.updateBiometricToken(token, (success) => {
                    if (success) {
                        onSuccess?.(token)
                    } else {
                        onError?.(new Error('Failed to set biometrics token'))
                    }
                })
            } else {
                onError?.(new Error('BiometricManager not available'))
            }
        } catch (error) {
            onError?.(error)
        }
    }

    /**
     * Initializes biometrics in the Telegram WebApp
     */
    static async biometricsInit() {
        try {
            const biometricManager = this.getBiometricManager()
            if (!biometricManager) {
                return false
            }
            
            return await new Promise<boolean>((resolve) => {
                biometricManager.init(() => resolve(true))
            })
        } catch (error) {
            return false
        }
    }

    static requestBiometricsAuth(onSuccess: (token: string) => void, onError: (error: any) => void) {
        try {
            const biometricManager = this.getBiometricManager()
            if (biometricManager) {
                biometricManager.authenticate({ reason: i18next.t('authentication.biometrics.requestVerification')}, (success, token) => {
                    if (success && token) {
                        onSuccess(token)
                    } else {
                        onError(success ? 'Token is undefined' : 'Biometrics authentication failed')
                    }
                })
            } else {
                onError('BiometricManager not available')
            }
        } catch (error) {
            onError(error)
        }
    }

    static requestBiometricsAccess(onSuccess?: () => void, onError?: (error: any) => void) {
        try {
            const biometricManager = this.getBiometricManager()
            if (biometricManager) {
                biometricManager.requestAccess({ reason: i18next.t('authentication.biometrics.requestAccess')}, (isGranted) => {
                    if (isGranted) {
                        onSuccess?.()
                        this.setBiometricsToken()
                    } else {
                        onError?.(new Error('Biometrics access denied'))
                    }
                })
            } else {
                onError?.(new Error('BiometricManager not available'))
            }
        } catch (error) {
            onError?.(error)
        }
    }

    /**
     * Checks if biometrics is initialized in the Telegram WebApp
     * @returns true if biometrics is initialized, false otherwise
     */
    static isBiometricsInited() {
        const biometricManager = this.getBiometricManager()
        return biometricManager ? biometricManager.isInited : false
    }

    static isAvailable() {
        const biometricManager = this.getBiometricManager()
        return biometricManager?.isBiometricAvailable ?? false
    }

    static isAccessGranted() {
        const biometricManager = this.getBiometricManager()
        if (biometricManager) {
            return biometricManager.isAccessGranted
        }
        return false
    }

    static openSettings() {
        try {
            if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.BiometricManager) {
                window.Telegram.WebApp.BiometricManager.openSettings();
            }
        } catch (error) {
            Sentry.captureException(error)
        }
    }

    static IsTokenSaved() {
        const biometricManager = this.getBiometricManager()
        if (biometricManager) {
            return biometricManager.isBiometricTokenSaved
        }
        return false
    }
}

export class SecureStorage {
    static async setItem(key: string, value: string): Promise<boolean> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return false
        }
        return new Promise((resolve) => {
            webApp.SecureStorage.setItem(key, value, (error: Error | null) => {
                if (error) {
                    // Sanitize error before sending to Sentry - don't include key or value
                    Sentry.captureException(new Error(`SecureStorage.setItem failed for key type: ${key.replace(/[^_]/g, '*')}`), {
                        contexts: {
                            storage: {
                                operation: 'setItem',
                                keyLength: key.length,
                                valueLength: value.length
                            }
                        }
                    })
                    resolve(false)
                } else {
                    resolve(true)
                }
            })
        })
    }

    static async getItem(key: string): Promise<string | null> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return null
        }
        return new Promise((resolve) => {
            webApp.SecureStorage.getItem(key, (error: Error | null, value: string | null) => {
                if (error) {
                    // Sanitize error before sending to Sentry - don't include key or value
                    Sentry.captureException(new Error(`SecureStorage.getItem failed for key type: ${key.replace(/[^_]/g, '*')}`), {
                        contexts: {
                            storage: {
                                operation: 'getItem',
                                keyLength: key.length
                            }
                        }
                    })
                    resolve(null)
                } else {
                    // Treat empty string as null (Android workaround)
                    const actualValue = (value === '' || value === null || value === 'null') ? null : value
                    resolve(actualValue)
                }
            })
        })
    }

    static async removeItem(key: string): Promise<boolean> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return false
        }
        if (!webApp.SecureStorage) {
            return false
        }

        const platform = webApp.platform

        // First attempt: Use removeItem API
        const removeResult = await new Promise<boolean>((resolve) => {
            webApp.SecureStorage.removeItem(key, (error: Error | null) => {
                if (error) {
                    // Sanitize error before sending to Sentry
                    Sentry.captureException(new Error(`SecureStorage.removeItem failed for key type: ${key.replace(/[^_]/g, '*')}`), {
                        contexts: {
                            storage: {
                                operation: 'removeItem',
                                keyLength: key.length
                            }
                        }
                    })
                    resolve(false)
                } else {
                    resolve(true)
                }
            })
        })

        // Android workaround only: Set empty string
        // On Android, removeItem sets value to null instead of deleting
        // On iOS, removeItem works correctly, so skip this step
        if (platform === 'android') {
            await new Promise<void>((resolve) => {
                webApp.SecureStorage.setItem(key, '', (error: Error | null) => {
                    if (error) {
                        // Sanitize error before sending to Sentry
                        Sentry.captureException(new Error(`SecureStorage.setItem (cleanup) failed for key type: ${key.replace(/[^_]/g, '*')}`))
                    }
                    resolve()
                })
            })
        }

        return removeResult
    }

    static async isSecureStorageSupported(): Promise<boolean> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return false
        }
        const canSave = await SecureStorage.setItem('test', 'test')
        const canRead = !!await SecureStorage.getItem('test')
        const canRemove = await SecureStorage.removeItem('test')
        return canSave && canRead && canRemove
    }
}

export class DeviceStorage {
    static async setItem(key: string, value: string): Promise<boolean> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return false
        }
        return new Promise((resolve) => {
            webApp.DeviceStorage.setItem(key, value, (error: Error | null) => {
                if (error) {
                    Sentry.captureException(error)
                    resolve(false)
                } else {
                    resolve(true)
                }
            })
        })
    }

    static async getItem(key: string): Promise<string | null> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return null
        }
        return new Promise((resolve) => {
            webApp.DeviceStorage.getItem(key, (error: Error | null, value: string | null) => {
                if (error) {
                    Sentry.captureException(error)
                    resolve(null)
                } else {
                    // Treat empty string as null (Android workaround)
                    const actualValue = (value === '' || value === null || value === 'null') ? null : value
                    resolve(actualValue)
                }
            })
        })
    }

    static async removeItem(key: string): Promise<boolean> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return false
        }
        if (!webApp.DeviceStorage) {
            return false
        }

        const platform = webApp.platform

        // First attempt: Use removeItem API
        const removeResult = await new Promise<boolean>((resolve) => {
            webApp.DeviceStorage.removeItem(key, (error: Error | null) => {
                if (error) {
                    Sentry.captureException(error)
                    resolve(false)
                } else {
                    resolve(true)
                }
            })
        })

        // Android workaround only: Set empty string
        // On Android, removeItem sets value to null instead of deleting
        // On iOS, removeItem works correctly, so skip this step
        if (platform === 'android') {
            await new Promise<void>((resolve) => {
                webApp.DeviceStorage.setItem(key, '', (error: Error | null) => {
                    if (error) {
                        Sentry.captureException(error)
                    }
                    resolve()
                })
            })
        }

        return removeResult
    }

    static async isDeviceStorageSupported(): Promise<boolean> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return false
        }
        const canSave = await DeviceStorage.setItem('test', 'test')
        const canRead = !!await DeviceStorage.getItem('test')
        const canRemove = await DeviceStorage.removeItem('test')
        return canSave && canRead && canRemove
    }
}

export class CloudStorage {
    static async setItem(key: string, value: string): Promise<boolean> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return false
        }
        
        // Use a Promise to handle the async nature of Telegram's CloudStorage API
        return await new Promise<boolean>((resolve, reject) => {
            webApp.CloudStorage.setItem(key, value, (error: Error | null) => {
                if (error) {
                    reject(false)
                } else {
                    resolve(true)
                }
            })
        })
    }

    static async getItem(key: string): Promise<string | null> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return null
        }
        
        // Get the encrypted value from CloudStorage
        // Use a Promise to handle the async nature of Telegram's CloudStorage API
        return await new Promise<string | null>((resolve) => {
            webApp.CloudStorage.getItem(key, (error: Error | null, value: string | null) => {
                if (error) {
                    resolve(null)
                } else {
                    resolve(value)
                }
            })
        })
    }

    static async removeItem(key: string): Promise<boolean> {
        const webApp = _getTelegramWebApp()
        if (!webApp) {
            return Promise.resolve(false)
        }
        
        // Use a Promise to handle the async nature of Telegram's CloudStorage API
        return new Promise<boolean>((resolve) => {
            webApp.CloudStorage.removeItem(key, (error: Error | null) => {
                if (error) {
                    resolve(false)
                } else {
                    resolve(true)
                }
            })
        })
    }
}